// Shared candle-timeframe option logic, reused by the bot Config page and
// both simulator forms so the "Candle Timeframe" dropdown stays consistent
// across every screen.
//
// V3 multi-market: the engine accepts ANY positive timeframe that divides
// the active market's window evenly. Rather than hardcode a per-window
// table (which drifted and, e.g., omitted 5 min on the 1 h window), we
// derive the options dynamically: offer every standard timeframe that
// divides the window evenly and yields more than one candle. This means the
// number of candles the operator can pick from is simply
// ``window_seconds / timeframe_seconds`` -- e.g. a 1 h window with a 5 min
// timeframe exposes 12 candles, with 1 min it exposes 60, and a 15 min
// window with 1 min exposes 15.

export interface CandleTimeframeOption {
  seconds: number;
  label: string;
}

// Canonical set of "nice" timeframes offered in the UI, smallest first.
// The engine is not limited to these, but they keep the dropdown meaningful.
const CANDIDATE_TIMEFRAMES: readonly CandleTimeframeOption[] = [
  { seconds: 60, label: "1 min" },
  { seconds: 300, label: "5 min" },
  { seconds: 600, label: "10 min" },
  { seconds: 900, label: "15 min" },
  { seconds: 1800, label: "30 min" },
  { seconds: 3600, label: "1 hr" },
];

/**
 * Human-readable label for an arbitrary timeframe width (seconds). Used for
 * values that fall outside the canonical candidate set (e.g. a legacy config
 * that stored an unusual timeframe).
 */
export function timeframeLabel(secs: number): string {
  const known = CANDIDATE_TIMEFRAMES.find((o) => o.seconds === secs);
  if (known) return known.label;
  if (secs % 3600 === 0) return `${secs / 3600} hr`;
  if (secs % 60 === 0) return `${secs / 60} min`;
  return `${secs}s`;
}

/**
 * Every standard timeframe that evenly divides ``windowSeconds`` and yields
 * more than one candle, smallest first. Always returns at least the 1 min
 * option so the dropdown is never empty.
 */
export function candleTimeframeOptionsFor(windowSeconds: number): CandleTimeframeOption[] {
  const win = Math.max(1, Math.floor(windowSeconds));
  const opts = CANDIDATE_TIMEFRAMES.filter(
    (o) => win % o.seconds === 0 && Math.floor(win / o.seconds) > 1,
  );
  return opts.length > 0 ? opts.map((o) => ({ ...o })) : [{ seconds: 60, label: "1 min" }];
}
